import React, { useState } from "react";
import { useLang } from "@/contexts/LanguageContext";
import api from "@/lib/api";
import { Mail, MapPin, Send, AlertTriangle, EyeOff, FileText } from "lucide-react";
import { toast } from "sonner";

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <input {...props} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none text-base min-h-[48px]" />
    </label>
  );
}
function TextArea({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-700">{label}</span>
      <textarea {...props} className="mt-1 w-full px-4 py-3 border border-slate-300 rounded-md focus:border-[#d81b7a] focus:ring-2 focus:ring-pink-200 outline-none text-base min-h-[120px]" />
    </label>
  );
}

export default function Contact() {
  const { t } = useLang();
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "" });
  const [griev, setGriev] = useState({ member_name: "", member_email: "", issue_type: "", description: "" });
  const [anon, setAnon] = useState({ category: "", description: "" });

  const submitContact = async (e) => {
    e.preventDefault();
    try { await api.post("/contact", contact); toast.success(t("contact.sent")); setContact({ name: "", email: "", subject: "", message: "" }); }
    catch { toast.error("Error"); }
  };
  const submitGrievance = async (e) => {
    e.preventDefault();
    try { await api.post("/grievances", griev); toast.success(t("contact.grievanceSent")); setGriev({ member_name: "", member_email: "", issue_type: "", description: "" }); }
    catch { toast.error("Error"); }
  };
  const submitAnon = async (e) => {
    e.preventDefault();
    try { await api.post("/anonymous-reports", anon); toast.success(t("contact.anonymousSent")); setAnon({ category: "", description: "" }); }
    catch { toast.error("Error"); }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-gradient-to-br from-pink-50 via-white to-rose-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900">{t("contact.title")}</h1>
          <p className="text-lg text-slate-600 mt-3 max-w-2xl">{t("contact.sub")}</p>
          <div className="h-1 w-20 cupe-gradient rounded-full mt-4" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-5">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-3 text-[#d81b7a] font-semibold mb-2"><Mail size={18} /> {t("contact.emailLabel")}</div>
            <a href="mailto:cupelocal4957oakpark@gmail.com" className="text-slate-700 hover:text-[#d81b7a] break-all">cupelocal4957oakpark@gmail.com</a>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-3 text-[#d81b7a] font-semibold mb-2"><MapPin size={18} /> {t("contact.officeLabel")}</div>
            <p className="text-slate-700">{t("contact.officeValue")}</p>
          </div>
          <div className="bg-pink-50 border border-pink-200 rounded-xl p-5">
            <div className="flex items-center gap-3 text-rose-700 font-semibold mb-2"><AlertTriangle size={18} /> {t("contact.emergencyTitle")}</div>
            <p className="text-slate-700 text-sm">{t("contact.emergencySub")}</p>
          </div>
        </div>

        <form onSubmit={submitContact} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 lg:col-span-2" data-testid="contact-form">
          <h2 className="font-display text-xl font-bold text-slate-900 mb-4">{t("contact.formTitle")}</h2>
          <div className="space-y-4">
            <Field label={t("contact.name")} required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name" />
            <Field label={t("contact.email")} type="email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email" />
            <Field label={t("contact.subject")} required value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} data-testid="contact-subject" />
            <TextArea label={t("contact.message")} required value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="contact-message" />
            <button type="submit" className="cupe-gradient text-white px-6 py-3 rounded-md font-semibold inline-flex items-center gap-2 min-h-[48px]" data-testid="contact-submit">
              {t("contact.send")} <Send size={16} />
            </button>
          </div>
        </form>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="contact-map-section">
        <div className="flex items-center gap-3 text-[#d81b7a] font-semibold mb-4">
          <MapPin size={20} />
          <h2 className="font-display text-xl font-bold text-slate-900">{t("contact.findUs")}</h2>
        </div>
        <p className="text-slate-600 mb-4">{t("contact.officeValue")}</p>
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
          <iframe
            title="CUPE Local 4957 Office Map"
            src="https://www.google.com/maps?q=2+Valor+Dr,+Ottawa,+ON&hl=en&z=16&output=embed"
            width="100%"
            height="420"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="contact-map-iframe"
          />
        </div>
        <a
          href="https://www.google.com/maps/dir/?api=1&destination=2+Valor+Dr,+Ottawa,+ON"
          target="_blank"
          rel="noreferrer"
          className="mt-4 inline-flex items-center gap-2 cupe-gradient text-white px-5 py-3 rounded-md font-semibold min-h-[48px]"
          data-testid="contact-directions-link"
        >
          {t("contact.getDirections")} <MapPin size={16} />
        </a>
      </section>

      <section id="grievance" className="bg-slate-50 border-y border-slate-200 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          <form onSubmit={submitGrievance} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid="grievance-form">
            <div className="flex items-center gap-3 mb-2"><FileText className="text-[#d81b7a]" /><h2 className="font-display text-xl font-bold text-slate-900">{t("contact.grievanceTitle")}</h2></div>
            <p className="text-sm text-slate-500 mb-4">{t("contact.grievanceSub")}</p>
            <div className="space-y-4">
              <Field label={t("contact.name")} required value={griev.member_name} onChange={(e) => setGriev({ ...griev, member_name: e.target.value })} data-testid="grievance-name" />
              <Field label={t("contact.email")} type="email" required value={griev.member_email} onChange={(e) => setGriev({ ...griev, member_email: e.target.value })} data-testid="grievance-email" />
              <Field label={t("contact.issueType")} required value={griev.issue_type} onChange={(e) => setGriev({ ...griev, issue_type: e.target.value })} data-testid="grievance-type" />
              <TextArea label={t("contact.description")} required value={griev.description} onChange={(e) => setGriev({ ...griev, description: e.target.value })} data-testid="grievance-desc" />
              <button type="submit" className="cupe-gradient text-white px-6 py-3 rounded-md font-semibold min-h-[48px]" data-testid="grievance-submit">{t("contact.submitGrievance")}</button>
            </div>
          </form>

          <form onSubmit={submitAnon} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8" data-testid="anonymous-form">
            <div className="flex items-center gap-3 mb-2"><EyeOff className="text-[#d81b7a]" /><h2 className="font-display text-xl font-bold text-slate-900">{t("contact.anonymousTitle")}</h2></div>
            <p className="text-sm text-slate-500 mb-4">{t("contact.anonymousSub")}</p>
            <div className="space-y-4">
              <Field label="Category / Catégorie" required value={anon.category} onChange={(e) => setAnon({ ...anon, category: e.target.value })} data-testid="anon-category" />
              <TextArea label={t("contact.description")} required value={anon.description} onChange={(e) => setAnon({ ...anon, description: e.target.value })} data-testid="anon-desc" />
              <button type="submit" className="cupe-gradient text-white px-6 py-3 rounded-md font-semibold min-h-[48px]" data-testid="anon-submit">{t("contact.submitAnonymous")}</button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
